// Clerk theming, in two parts.
//
// 1. `clerkAppearance` (global, on <ClerkProvider>): variables that make every
//    Clerk surface (sign-in modal, embedded profiles) match the app: squared
//    corners, the --comp-* palette, Inter. Clerk renders these in portals under
//    <body>, where the :root CSS variables still cascade, so referencing the
//    tokens keeps one source of truth with the rest of the theme.
//
// 2. `settingsEmbedAppearance` (per-component, on the embedded <UserProfile/> /
//    <OrganizationProfile/>): maps Clerk's internal elements to OUR own
//    classNames (defined in SettingsPage.css). Clerk supports className strings
//    on `appearance.elements`; combined with `cssLayerName` below, our
//    unlayered CSS overrides Clerk's defaults without `!important`. This is how
//    we hide Clerk's own navbar/header (our side nav drives the pages instead)
//    and square the avatar so the embeds blend into the Settings shell.
//    Menu/select classes also land on portaled popovers (rendered under <body>),
//    so Settings dropdowns still get the markets-style ink list outside `.clerk-embed`.
//
// `cssLayerName: 'clerk'` puts Clerk's injected styles in the `clerk` CSS layer
// (declared before Tailwind's layers in index.css), so any unlayered rule wins.
//
// NOTE: the color `variables` must be concrete hex, NOT `var(--token)`. Clerk
// derives hover/active/border shades by doing color math on these values; given
// a `var()` string it can't parse, hover states collapse to solid dark-on-dark.
// These mirror the --comp-*/--fg-* tokens in adj.news index.css; keep in sync.
export const clerkAppearance = {
  cssLayerName: 'clerk',
  variables: {
    colorPrimary: '#0a0f0d', // --comp-ink
    colorBackground: '#ffffff', // --comp-paper
    colorText: '#0a0f0d', // --comp-ink
    colorTextSecondary: '#5c5a53', // --fg-2
    colorInputBackground: '#ffffff', // --comp-paper
    colorInputText: '#0a0f0d', // --comp-ink
    colorNeutral: '#0a0f0d', // --comp-ink
    borderRadius: '0',
    fontFamily: 'var(--font-main)',
  },
  elements: {
    card: { boxShadow: 'none', border: '1px solid #d6d2c8', backgroundColor: '#ffffff' },
    modalContent: { boxShadow: 'none' },
    navbar: { borderRight: '1px solid #d6d2c8' },
    // Always light: Clerk modals stay paper/ink even when the app is dark.
    // Concrete hex (not var()) so Clerk's color math for hover/focus stays valid.
    formFieldInput: {
      backgroundColor: '#ffffff',
      color: '#0a0f0d',
      borderColor: 'rgba(10, 15, 13, 0.14)',
    },
    formButtonPrimary: {
      backgroundColor: '#0a0f0d',
      color: '#ffffff',
    },
    footer: {
      backgroundColor: '#ffffff',
    },
  },
};

// Embedded-profile overrides: hide Clerk's own chrome (navbar + page header) so
// our side nav is the only nav, and blend the card into our content column.
export const settingsEmbedAppearance = {
  elements: {
    rootBox: 'clerk-embed',
    cardBox: 'clerk-embed-cardbox',
    card: 'clerk-embed-card',
    navbar: 'clerk-embed-hide',
    navbarMobileMenuButton: 'clerk-embed-hide',
    header: 'clerk-embed-hide',
    scrollBox: 'clerk-embed-scrollbox',
    avatarBox: 'clerk-embed-avatar',
    userPreviewAvatarBox: 'clerk-embed-avatar',
    // Markets-style dropdowns (portaled; the class must work outside `.clerk-embed`).
    selectButton: 'settings-clerk-select-trigger',
    selectButtonIcon: 'settings-clerk-hide-arrow',
    selectOptionsContainer: 'settings-clerk-menu',
    selectOption: 'settings-clerk-menu-item',
    menuButton: 'settings-clerk-menu-trigger',
    menuList: 'settings-clerk-menu',
    menuItem: 'settings-clerk-menu-item',
    popoverBox: 'settings-clerk-menu',
    // `tabListContainer` is the real element key; there is no `tabList` key in
    // @clerk/react's appearance map, so mapping that name renamed nothing and
    // left Clerk's own hairline under the Members/Invitations strip unstyled.
    tabListContainer: 'settings-clerk-tab-list',
    tabButton: 'settings-clerk-tab',
  },
};
